import React, {Dispatch, lazy, Suspense, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {UserFlowStep} from '../../Facade/UserFlow/BaseUserFlow';
import {useUserFlowState} from '../Context/UserFlow/UserFlowContext';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {useTaopix} from '../Context/TaopixContext';
import {useExperience} from '../Experience/ExperienceProvider';
import APIServer from '@taopix/taopix-core/dist/types/Service/APIServer';
import ComponentService from '../../Service/Components/ComponentsService';
import {
  AIMode,
  Common,
  Component,
  CoreActions,
  CurrencyAdapter,
  ExternalAssetAdapter,
  Lang,
  Localisation,
  SettingsActions,
} from '@taopix/taopix-core';
import useSettings from '../../Hooks/useSettings';
import {useDispatch, useSelector} from 'react-redux';
import {
  OptionsAndExtrasCloseMode,
  OptionsAndExtrasDisplayStage,
  ThreeDPreviewStatus,
} from '../../Enums';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';
import {
  ChargableEventSetSelector,
  ExternalAssetSelector,
  LayoutSelector,
  TotalPageCountSelector,
} from '../../Selectors';
import {ActionType} from 'typesafe-actions';
import {LoadingOverlay, ThemeName} from '@taopix/taopix-design-system';
import {OAE_SIDEBAR_MIN_COMPONENTS} from '../../Constants';
import {ComponentTree, ObjectInterface, ReducerState} from '@taopix/taopix-core/dist/types/types';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {PricingEngine} from '@taopix/taopix-core/dist/types/PricingEngine/PricingEngine';

// options and extras dialog
const OptionsAndExtras = lazy(() => import('../OptionsAndExtras/OptionsAndExtras'));

type OptionsAndExtrasWrapperProps = {
  server: APIServer;
};

enum ThumbnailStatus {
  None,
  Loading,
  Loaded,
}

const OptionsAndExtrasWrapper = ({server}: OptionsAndExtrasWrapperProps) => {
  const taopix = useTaopix();
  const userFlow = useUserFlowState();
  const ui = useUiState();
  const settings = useSettings();
  const {experience, getAIMode} = useExperience();
  const {t} = useTranslation(['optionsandextras']);
  const productLayout = useSelector(LayoutSelector);
  const pageCount = useSelector(TotalPageCountSelector);
  const externalAssets = useSelector(ExternalAssetSelector);
  const calendarEvents: any = useSelector<any>(state => state.project?.calendar?.events) || {};
  const calendarEventSets: any = useSelector<any>(ChargableEventSetSelector) || {};
  const aiMode = getAIMode();
  const [thumbnailData, setThumbnailData] = useState<string>('');
  const open = userFlow.isUserFlowActive(UserFlowType.OptionsAndExtras);
  const thumbnailStatus = useRef<ThumbnailStatus>(ThumbnailStatus.None);
  const dispatch = useDispatch<Dispatch<ActionType<typeof CoreActions>>>();

  const updatePricing = (data: ObjectInterface): void => {
    settings.dispatch(
      SettingsActions.updatePricing({
        quantity: data.product.quantity,
        components: data.product.componentTree,
      })
    );
  };

  /**
   * Saves the selected component tree to the database.
   *
   * @param pricingEngine PricingEngine instance.
   * @param productQuantity The product quantity.
   * @param perfectlyClearApplied True if Perfectly Clear has been applied.
   * @param componentTree The full component tree to save.
   */
  const saveComponentSelection = (
    pricingEngine: PricingEngine,
    productQuantity: number,
    perfectlyClearApplied: boolean,
    componentTree: ComponentTree
  ): Promise<any> => {
    const componentService: ComponentService = new ComponentService(server, pricingEngine);

    // Trigger the component selection save.
    return componentService
      .saveComponentSelection(productQuantity, perfectlyClearApplied, componentTree)
      .catch((error: string) => {
        console.error(error);
      });
  };

  const getProjectThumbnail = () => {
    thumbnailStatus.current = ThumbnailStatus.Loading;
    ui.dispatch(UserInterfaceActions.setIsBusy(true));
    if (ThreeDPreviewStatus.Loaded === ui.state.threeDPreviewStatus) {
      setThumbnailData(taopix.core.get('threeDPreviewManager').getSnapshot(500, 500));
      thumbnailStatus.current = ThumbnailStatus.Loaded;
      ui.dispatch(UserInterfaceActions.setIsBusy(false));
    } else {
      taopix.core
        .get('stateManager')
        .call<{}, ReducerState & {thumbnail: string}>('projectthumbnail', {width: 500, height: 500})
        .then(response => {
          setThumbnailData(response.thumbnail);
          thumbnailStatus.current = ThumbnailStatus.Loaded;
          ui.dispatch(UserInterfaceActions.setIsBusy(false));
        });
    }
  };

  useEffect(() => {
    if (open && taopix.state.loaded) {
      getProjectThumbnail();
    }
  }, [open]);

  const displayStage = (() => {
    switch (userFlow.getStateStage()) {
      case UserFlowType.Start: {
        return OptionsAndExtrasDisplayStage.Start;
      }
      case UserFlowType.Order: {
        return OptionsAndExtrasDisplayStage.Order;
      }
      default: {
        return OptionsAndExtrasDisplayStage.All;
      }
    }
  })();

  const showComponents = useMemo(() => {
    switch (displayStage) {
      case OptionsAndExtrasDisplayStage.Start: {
        return experience.EDITOR.componentOptions.showComponentOptionsOnOpen;
      }
      case OptionsAndExtrasDisplayStage.Order: {
        return experience.EDITOR.componentOptions.showComponentOptionsOnOrder;
      }
      case OptionsAndExtrasDisplayStage.All: {
        return true;
      }
    }
  }, [open, userFlow.getStateStage()]);

  if (!taopix.state.loaded) return <></>;

  // Wait for preview, we might need to load additional slice data.
  if (open && thumbnailStatus.current < ThumbnailStatus.Loaded) {
    return (
      <LoadingOverlay
        id={'oae-loading-overlay'}
        open={true}
        label={t('*:str_MessagePleaseWait')}
        theme={ThemeName.Popouts}
        overlayColour={'transparent'}
      />
    );
  }

  return (
    <Suspense fallback={<LoadingOverlay open={true} usePortal={true} />}>
      <OptionsAndExtras
        isOpen={open}
        isSmallScreen={ui.state.isSmallScreen}
        componentService={ComponentService}
        helperFunctions={{
          t: t,
          deepClone: taopix.core.get('utils').deepClone,
          getLocaleString: Localisation.getLocaleString,
          getCalendarCustomisationCount: () => {
            return {
              days: Object.keys(calendarEvents).length,
              sets: Object.keys(calendarEventSets).length,
            };
          },
        }}
        showComponents={showComponents}
        languageCode={Lang.getCode()}
        canValidate={UserFlowType.Order === userFlow.getStateStage()}
        pricingEngine={taopix.core.get('pricingEngine')}
        settings={{
          assets: {
            external: ExternalAssetAdapter(externalAssets, productLayout),
          },
          perfectlyClear: {
            available: experience.SETTINGS.autoImageEnhancement.allowCustomersToOptOut,
            applied:
              settings.state.price.pricing.perfectlyclear ??
              experience.SETTINGS.autoImageEnhancement.enhanceAllUserImagesAuto,
          },
          pricing: {
            aiChargable: AIMode.On === aiMode,
            hasLivePricing: settings.state.price.showPrice,
            disclaimer: {
              text: Common.getCurrentLocaleString(settings.state.price.disclaimer, true),
              link: {
                url: settings.state.price.infoUrl,
              },
            },
            currency: CurrencyAdapter(settings.state.currency),
          },
          product: {
            projectName: settings.state.project.name,
            allowChangeQuantity: experience.SETTINGS.orderSummaryWindow.showProjectQuantityDropdown,
            quantity: settings.state.price.pricing.quantity || 0,
            layout: {...productLayout, pricing: settings.state.price.pricing, pagecount: pageCount},
            preview: thumbnailData,
          },
        }}
        displayStage={displayStage}
        showSidebar={(() => {
          if (ui.state.isSmallScreen) {
            return false;
          }

          if ([UserFlowType.UserFlow].includes(userFlow.getStateStage())) {
            return true;
          }

          const pricing = settings.state.price.pricing;

          const productComponents =
            pricing !== null && pricing.components.hasOwnProperty('PRODUCT')
              ? Component.flattenComponentTree(
                  Object.values(pricing.components.PRODUCT?.children || [])
                ).filter(component => {
                  return (
                    ['TAOPIXAI', 'CALENDARCUSTOMISATION'].indexOf(component.sectioncode) === -1 &&
                    (OptionsAndExtrasDisplayStage.All === displayStage ||
                      ((component.displaystage as OptionsAndExtrasDisplayStage) & displayStage) ===
                        displayStage)
                  );
                })
              : [];

          return productComponents.length >= OAE_SIDEBAR_MIN_COMPONENTS;
        })()}
        closeMode={(() => {
          switch (userFlow.getStateStage()) {
            case UserFlowType.Order: {
              return OptionsAndExtrasCloseMode.Order;
            }
            default: {
              return OptionsAndExtrasCloseMode.Close;
            }
          }
        })()}
        onSaveAndClose={() => {
          userFlow.setCurrentStep(`${UserFlowType.OptionsAndExtras}.${UserFlowStep.None}`);
        }}
        onClose={data => {
          updatePricing(data);
          dispatch(CoreActions.setApplyPerfectlyClear(data.perfectlyClear));

          saveComponentSelection(
            data.pricingEngine,
            data.product.quantity,
            data.perfectlyClear,
            data.product.componentTree
          ).then(() => {
            userFlow.setNextStep();
          });
        }}
        onSetProjectTotal={(projectPrice, data) => {
          // Only run if Options & Extras is open and live pricing is on.
          // Otherwise, pricing will update after the Options & Extras closes.
          if (
            userFlow.isUserFlowActive(UserFlowType.OptionsAndExtras) &&
            settings.state.price.showPrice
          ) {
            updatePricing(data);
          }
        }}
      />
    </Suspense>
  );
};

export default OptionsAndExtrasWrapper;
